<script setup>
import { useFeedbackEntry } from '@/@core/stores/feedback-entry'
import { titleCase } from '@/@core/utils/formatters'
import { cookieRef } from '@/@layouts/stores/config'
import FeedbackQuestionFormV2 from '@/components/feedback/FeedbackQuestionFormV2.vue'
import { userAuthState } from '@core/auth'
import GoogleTranslateSelect from '@google-translate-select/vue3'
import { themeConfig } from '@themeConfig'
import { nextTick, ref } from 'vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import { SourceType } from '@/enums/feedback/source.type'
import { useRouter, useRoute } from 'vue-router'
import { isFeedbackPublicFormOneTimeSubmission } from '@/@core/utils/unitConfig'
import otpService from '@/services/otp.service'
import { VOtpInput } from 'vuetify/labs/VOtpInput'
import { useTenant } from '@/composables/useTenant'

definePage({ 
  meta: { 
    layout: 'client', 
    public: true, 
    unauthenticatedOnly: true,
    title: 'Write Feedback',
  }, 
})


const auth = userAuthState()
const router = useRouter()
const route = useRoute()
const google_map_link = ref(null)
const feedbackId = ref(null)

// we need to take source type form type and episode number from the url
const { s: sourceType, f: formType, e: episodeNumber, l: locationNo, u: unitValue } = route.query
const episodeNoValue =ref(null)

const location = ref(null)
const unitId = ref(null)
const sourceTypeValue = ref(null)
const formTypeValue = ref(null)
const isFeedbackPositive = ref(false)
const contactForm = ref()

try {
  episodeNoValue.value = episodeNumber ? atob(episodeNumber) : null
  location.value = locationNo ? atob(locationNo) : null
  sourceTypeValue.value = sourceType ? atob(sourceType) : null
  formTypeValue.value = formType ? atob(formType) : null
  unitId.value = unitValue ? atob(unitValue) : 1 // * Default to 1 if unit is not provided, required for sarvodaya
} catch (error) {
  console.error('Error parsing URL:', error)
  router.push({ name: 'invalid-url', params: { code: 404,
    message: 'Invalid URL. Please try again with a valid URL.',
  
  } })
}

const feedbackData = reactive({
  formType: null,
  name: '',
  email: '',
  location: '',
  phone: '',
  uhid: '',
  episode_no: '',
  consent: false,
  feedback: [],
  unit_id: null,
  doctor_name: null,
})

const hasCustomerType = ref(false)

const hasOtpVerification = computed(() => {
  return unit.value?.config_heads?.some(ch => ch?.type === 'feedback_collection' && ch?.key === 'otp_verification') ?? false
})

const otpVerified = ref(false)
const otpVerifiedPhone = ref('')
const otpValue = ref('')
const otpLoading = ref(false)
const otpError = ref('')
const otpCooldown = ref(0)
let otpCooldownInterval = null

const beforeUnloadHandler = e => {
  if (currentStep.value > 0 && currentStep.value < successStep.value) {
    e.preventDefault()
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', beforeUnloadHandler)
})

onUnmounted(() => {
  clearInterval(otpCooldownInterval)
  window.removeEventListener('beforeunload', beforeUnloadHandler)
})

const unit = ref(null)

if (!unitId.value) {
  router.push({ name: 'invalid-url', params: { code: 404 } })
} else {
  await useApi(createUrl('public/units', 
    { 
      query: { 
        search: {
          id: unitId.value,
        },
      },
    },
  ), {
    afterFetch: response => {
      if (response?.data?.length === 0) {
        router.push({ name: 'invalid-url', params: { code: 404 } })
      }
      unit.value = response.data[0]
      hasCustomerType.value = response.data[0]?.config_heads?.some(ch => ch?.type === 'feedback_collection' && ch?.key === 'customer_type') ?? false
      google_map_link.value = response.data[0]?.google_map_link
      feedbackData.unit_id = unitId.value
    },
  })
}

const source = ref()

// if source type is not present in the url then throw error
if (!sourceTypeValue.value) {
  router.push({ name: 'invalid-url', params: { code: 404 } })
} else {
  await useApi(createUrl('public/feedback-sources', 
    { 
      query: { 
        search: {
          status: 1,
          id: sourceTypeValue,
        },
      },
    },
  ), {
    afterFetch: response => {
      if (response?.data?.length === 0) {
        router.push({ name: 'invalid-url', params: { code: 404 } })
      }

      source.value = response.data[0]
      feedbackData.sourceType = source.value?.id

      /**
       * * Check & Validate Feedback Submission
       * If source type is QrCode
       * If last feedback submitted at is equal to current date
      */
      if (
        isFeedbackPublicFormOneTimeSubmission(unit.value.config_heads) &&
        source.value.source_type == SourceType.QR_CODE &&
        localStorage.getItem('last_feedback_submitted_date') == new Date().toISOString().split('T')[0]
      ) {
        router.push({ 
          name: 'invalid-url',
          params: { 
            code: 400,
            title: 'Feedback Already Submitted !',
            description: 'You have already submitted feedback.',
          },
        })
      }
    },
  })
}

// if form type is present validate it
if (formTypeValue.value) {
  await useApi(createUrl('public/form-types', 
    { 
      query: { 
        search: {
          status: 1,
          id: formTypeValue,
        },
        unit_id: unitId.value,
      },
    },
  )).then(response => {
    if (response?.data?.value?.length === 0) {
      router.push({ name: 'invalid-url', params: { code: 404 } })
    }
    feedbackData.formType = response.data?.value[0]?.id
  })
}

// if episode number is present validate it
if (episodeNoValue.value) {
  feedbackData.episode_id = episodeNoValue
}

// Handle answers update from FeedbackQuestionFormV2
const handleAnswersUpdate = formattedAnswers => {
  // Replace entire feedback array with formatted answers from V2 component
  feedbackData.feedback = formattedAnswers
}

// NPS value for conditional question visibility
const npsValue = computed(() => {
  const npsAnswer = feedbackData.feedback.find(a => a.questionKey === 'NPS')

  return npsAnswer?.response?.label ?? npsAnswer?.response?.value ?? npsAnswer?.response ?? null
})

const currentStep = ref(0)

const otpOffset = computed(() => hasOtpVerification.value ? 1 : 0)
const questionsStep = computed(() => 3 + otpOffset.value)
const successStep = computed(() => 4 + otpOffset.value)

const { isFetchingSourceTypes, data: feedbackTypeRadio } =await useListFetchingService('public/form-types', 
  { 
    query: { 
      select: ['id', 'name', 'color'],
      search: {
        status: 1,
      },
      unit_id: unitId.value,
    },
  },
)

const selectedFormType =computed(() => {  
  return feedbackData.formType
})

const selectedFormTypeName =computed(() => {
  return feedbackTypeRadio.value?.find(item => item.id === feedbackData?.formType)
})

const beforeFetch = async ({ url, options, cancel }) =>{
  if (!feedbackData?.formType) {
    cancel()
  }
}

const applicableCustomerType = computed(() => 
  feedbackData?.customer_type === 'Corporate'
    ? 'Corporate'
    : '',
)

const { isFetching: fetchingQuestions, execute: refetchQuestions, data: questions } = useApi(createUrl('public/feedback/questions', {
  query: {
    ep: episodeNoValue.value??null,
    search: {
      status: 1,
      form_type_id: selectedFormType,
    },
    applicable_customer_type: applicableCustomerType,
  },
}),
{
  beforeFetch,
  onFetchError(ctx) {
    const errorCode = ctx.error?.errors?.error?.[0]
    if (errorCode === 'FEEDBACK_ALREADY_SUBMITTED') {
      router.push({
        name: 'invalid-url',
        params: {
          code: 400,
          title: 'Feedback Already Submitted !',
          description: 'You have already submitted feedback.',
        },
      })
    } else {
      router.push({
        name: 'invalid-url',
        params: {
          code: ctx.response?.status ?? 500,
          title: 'Something Went Wrong !',
        },
      })
    }

    return ctx
  },
},
)

const questionsComputed = computed(() => {
  return questions.value
})

watch(
  () => [feedbackData.customer_type, selectedFormType],
  async ([custType, formType]) => {
    if (custType && formType) {
      await refetchQuestions()
    }
  },
  { immediate: false },
)

const feedbackForm =ref()

const checkCurrentStep = computed(() => {
  if (currentStep.value === 0) {
    return feedbackData.consent
  }
  if (currentStep.value === 1) {
    return feedbackData.formType
  }
  if (currentStep.value === 2) {
    return feedbackData.name && feedbackData.phone
  }
  if (hasOtpVerification.value && currentStep.value === 3) {
    return otpVerified.value
  }
  if (currentStep.value === questionsStep.value) {
    return !fetchingQuestions.value
  }

  return true
})

const buttonLabel = computed(() => {
  return currentStep.value === questionsStep.value ? 'Submit Feedback' : 'Continue'
})


const submitBtnLoading = ref(false)

// const maskedPhone = computed(() => {
//   const phone = feedbackData.phone || ''

//   if (phone.length <= 5) return phone

//   const prefix = phone.slice(0, 5)
//   const suffix = phone.slice(-3)
//   const masked = '*'.repeat(phone.length - 7)

//   return prefix + masked + suffix
// })

const startOtpCooldown = (seconds = 60) => {
  otpCooldown.value = seconds
  clearInterval(otpCooldownInterval)
  otpCooldownInterval = setInterval(() => {
    otpCooldown.value--
    if (otpCooldown.value <= 0) {
      clearInterval(otpCooldownInterval)
    }
  }, 1000)
}

const sendOtp = async () => {
  otpLoading.value = true
  otpError.value = ''
  otpValue.value = ''
  try {
    const response = await otpService.sendOtp(
      feedbackData.phone,
      'feedback_verification',
      unitId.value,
      { user_name: feedbackData.name },
    )

    if (response?.success) {
      startOtpCooldown()

      return true
    }
    otpError.value = response?.message || 'Failed to send OTP. Please try again.'

    return false
  } catch (error) {
    otpError.value = 'Failed to send OTP. Please try again.'

    return false
  } finally {
    otpLoading.value = false
  }
}

const handleOtpVerify = async () => {
  if (otpValue.value.length !== 6) return

  otpLoading.value = true
  otpError.value = ''
  try {
    const response = await otpService.verifyOtp(
      feedbackData.phone,
      'feedback_verification',
      otpValue.value,
    )

    if (response?.success) {
      otpVerified.value = true
      otpVerifiedPhone.value = feedbackData.phone
      currentStep.value++

      return
    }

    if (response?.error === 'max_attempts_exceeded') {
      otpError.value = 'Too many failed attempts. Please request a new code.'
    } else if (response?.error === 'otp_not_found') {
      otpError.value = 'Code expired. Please request a new code.'
    } else {
      otpError.value = `Incorrect code. ${response?.attempts_remaining ?? 0} attempts remaining.`
    }
  } catch (error) {
    otpError.value = 'Verification failed. Please try again.'
  } finally {
    otpLoading.value = false
  }
}

const resendOtp = async () => {
  if (otpCooldown.value > 0) return

  otpLoading.value = true
  otpError.value = ''
  otpValue.value = ''
  try {
    const response = await otpService.resendOtp(
      feedbackData.phone,
      'feedback_verification',
      unitId.value,
      { user_name: feedbackData.name },
    )

    if (response?.success) {
      startOtpCooldown()
    } else if (response?.error === 'cooldown_active') {
      startOtpCooldown(response?.cooldown_remaining ?? 60)
    } else {
      otpError.value = 'Failed to resend OTP. Please try again.'
    }
  } catch (error) {
    otpError.value = 'Failed to resend OTP. Please try again.'
  } finally {
    otpLoading.value = false
  }
}

const nextStep = () => {
  switch (currentStep.value) {
  case 0:
    if (feedbackData.formType) {
      currentStep.value += 2

      if (feedbackData.episode_id) {
        currentStep.value++
      }
      break
    }
    currentStep.value++

    break

  case 2:
    if (feedbackData.episode_id) {
      if (hasOtpVerification.value && !otpVerified.value) {
        currentStep.value = 3
        sendOtp()
        break
      }
      currentStep.value = questionsStep.value
      break
    }
    contactForm.value?.validate().then(async ({ valid: isValid }) => {
      if (isValid) {
        if (hasOtpVerification.value && !otpVerified.value) {
          currentStep.value = 3
          sendOtp()
        } else {
          currentStep.value = questionsStep.value
        }
      }
    })
    break

  default:
    if (currentStep.value === questionsStep.value) {
      feedbackForm.value?.validate().then(async ({ valid: isValid }) => {
        if (!isValid) return

        submitBtnLoading.value = true
        await useFeedbackEntry().updateCurrentFeedback({
          customer_type: (feedbackData?.uhid || feedbackData?.episode_id) ? 'PATIENT' : 'VISITOR',
          source_type: feedbackData.sourceType,
          form_type: feedbackData.formType,
          location_name: feedbackData.location,
          customer: {
            name: feedbackData.name,
            email: feedbackData.email,
            phone: feedbackData.phone,
            uhid: feedbackData.uhid,
            episode_no: feedbackData.episode_no,
            customer_id: feedbackData?.customer_id,
            customer_type: feedbackData?.customer_type,
            episode_id: feedbackData?.episode_id,
            location_id: location.value,
            feedback_datetime: new Date().toISOString(),
            doctor_name: feedbackData?.doctor_name,
          },
          unit_id: feedbackData.unit_id ?? cookieRef('selectedUnit'),
          feedback: feedbackData.feedback,
        })

        isFeedbackPositive.value = feedbackData.feedback.some(
          item => item.questionKey === 'NPS' && item.response?.label > 8,
        )

        await nextTick()

        useFeedbackEntry().submitCurrentFeedback(true, data => {
          submitBtnLoading.value = false
          currentStep.value = successStep.value
          feedbackId.value = data?.id

          if (
            isFeedbackPublicFormOneTimeSubmission(unit.value.config_heads) &&
            feedbackId.value &&
            source.value.source_type == SourceType.QR_CODE
          ) {
            localStorage.setItem('last_feedback_submitted_date', new Date().toISOString().split('T')[0])
          }
        })
      })
      break
    }
    currentStep.value += 1
  }
}

const handleError = error => {
  console.error('Translation error:', error)
}

const handleChange = lang => {
  if (!lang) return
  
  const googleFrame = document.querySelector('.goog-te-combo')
  if (googleFrame) {
    googleFrame.value = lang.code
    googleFrame.dispatchEvent(new Event('change'))
  }
}



watch(feedbackData, () => {
  if (feedbackData && Array.isArray(feedbackData?.feedback) && feedbackData?.feedback?.length > 0){
    useFeedbackEntry().updateCurrentFeedback({
      feedback: feedbackData.feedback?.filter(item => {
        if (item.response !== null && item.response !== '' && item.response !== undefined) {
          return item
        }
      }),
    })
  }
}, {
  deep: true,
  immediate: true,
})

watch(
  () => feedbackData.phone,
  newPhone => {
    otpVerified.value = newPhone === otpVerifiedPhone.value
    otpValue.value = ''
    otpError.value = ''
  },
)

const previousStep = () => {
  if (hasOtpVerification.value && currentStep.value === 3) {
    otpValue.value = ''
    otpError.value = ''
    currentStep.value = 2

    return
  }
  if (currentStep.value === questionsStep.value) {
    currentStep.value = 2

    return
  }
  if (currentStep.value === 2 && formTypeValue.value) {
    currentStep.value = currentStep.value - 2

    return
  }
  currentStep.value = currentStep.value - 1
}

const googleTranslateSelect =ref()

const languages = ref([
  {
    code: 'en',
    L: 'en',
    name: 'English',
    cname: '英语',
    ename: 'English',
  },
  {
    code: 'hi',
    L: 'hi',
    name: 'हिंदी',
    cname: '印地语',
    ename: 'Hindi',
  },
  {
    code: 'ml',
    L: 'ml',
    name: 'മലയാളം',
    cname: '马拉雅拉姆语',
    ename: 'Malayalam',
  },
  {
    code: 'kn',
    L: 'kn',
    name: 'ಕನ್ನಡ',
    cname: '卡纳达语',
    ename: 'Kannada',
  },
])


const customerTypes = await getEnumValue('FeedbackCustomerTypes')

customerTypes.value.unshift({
  title: 'Normal',
  value: null,
})

const hasDoctorName = computed(() => {
  return unit.value?.config_heads?.some(ch => ch?.type === 'feedback_collection' && ch?.key === 'doctor_name') ?? false
})

const {
  data: doctors,
  isFetching: fetchingDoctors,
} = useApi(createUrl('public/doctors', {
  query: {
    search: {
      unit_id: unitId.value,

      // form_type_id: formTypeValue.value,
    },
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!hasDoctorName.value) cancel()
  },
})

const { isTenant } = useTenant()

const googleBtnLoading = ref(false)

const openInExternalBrowser = url => {
  const ua = navigator.userAgent || ''
  const isAndroid = /android/i.test(ua)
  const isIOS = /iPhone|iPad|iPod/.test(ua)

  if (isAndroid) {
    try {
      const urlObj = new URL(url)
      const intentUrl = `intent://${urlObj.host}${urlObj.pathname}${urlObj.search}${urlObj.hash}#Intent;scheme=${urlObj.protocol.replace(':', '')};action=android.intent.action.VIEW;end`

      window.location.href = intentUrl
    } catch {
      window.open(url, '_blank')
    }
  } else if (isIOS) {
    // Try Chrome first (googlechromes:// for https, googlechrome:// for http)
    const chromeUrl = url.replace(/^https:\/\//, 'googlechromes://').replace(/^http:\/\//, 'googlechrome://')

    // // Safari fallback mechanism (commented out)
    // const fallbackTimeout = setTimeout(() => {
    //   document.removeEventListener('visibilitychange', onVisibilityChange)
    //   window.location.href = url.replace(/^https:\/\//, 'x-safari-https://')
    // }, 1500)

    // const onVisibilityChange = () => {
    //   if (document.hidden) {
    //     clearTimeout(fallbackTimeout)
    //     document.removeEventListener('visibilitychange', onVisibilityChange)
    //   }
    // }

    // document.addEventListener('visibilitychange', onVisibilityChange)
    window.location.href = chromeUrl
  } else {
    window.open(url, '_blank')
  }
}

const googleBtnClick = () => {
  if (feedbackId.value) {
    googleBtnLoading.value = true
    $api(`public/feedback/update-google-review-action`,
      {
        method: 'PUT',
        body: {
          feedback_id: feedbackId.value,
        },
      })
      .finally(() => {
        googleBtnLoading.value = false
        openInExternalBrowser(google_map_link.value)
      })
  } else {
    googleBtnLoading.value = false
    openInExternalBrowser(google_map_link.value)
  }
}
</script>

<template>
  <div class="feedback-card feedback-container mx-auto pt-6 pt-sm-10">
    <VCard variant="flat">
      <VWindow
        v-model="currentStep"
        class="disable-tab-transition"
        :touch="false"
      >
        <!-- Notice -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <div
              v-if="languages && languages?.length"
              class="translate-wrapper"
            >
              <GoogleTranslateSelect
                ref="googleTranslateSelect"
                default-language-code="en"
                default-page-language-code="en"
                :fetch-browser-language="false"
                trigger="click"
                class="border rounded"
                :languages="languages"
                @error="handleError"
                @change="handleChange"
              />
            </div>
          </VCardText>
          <VCardText class="px-4 px-sm-6">
            <div class="text-center text-primary text-h5 mb-5 ">
              Welcome To {{ unit?.display_name ?? themeConfig.app.client.name }}
            </div>

            <template v-if="unit.feedback_form_greeting_message">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="unit.feedback_form_greeting_message" />
            </template>
            <template v-else>
              <div class="text-body-1">
                <p>
                  Dear Sir / Madam,
                </p>
                <p>
                  Thank you for choosing {{ titleCase(unit?.display_name ?? themeConfig.app.client.name) }} as your center of choice for treatment. 
                       
                  We would like to know your feedback on the services provided by us. 
                  Your feedback will help us to improve our services and serve you better.
                  We appreciate your feedback!
                </p>
              </div>
            </template>

            <VAlert
              variant="tonal"
              color="grey-400"
            >
              <div class="d-flex align-start">
                <VCheckbox
                  v-model="feedbackData.consent"
                  color="primary"
                >
                  <template #label>
                    <span>
                      I hereby authorize {{ unit?.display_name }} to use this information for public relation effort or for any other lawful purpose.
                      <a
                        v-if="unit?.privacy_policy"
                        :href="unit?.privacy_policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </template>
                </VCheckbox>
              </div>
            </VAlert>
          </VCardText>
        </VWindowItem>

        <!-- Feedback Form Type -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <div class="text-center text-primary text-h5 mb-4">
              Select The Service you want to provide feedback for
            </div>
                    
            <div
              v-if="!isFetchingSourceTypes"
              class="d-flex flex-wrap justify-center gap-3"
            >
              <VBtn
                v-for="item in feedbackTypeRadio"
                :key="item.id"
                size="large"
                :color="item.color || 'primary'"
                :variant="feedbackData.formType === item.id ? 'flat' : 'tonal'"
                @click="feedbackData.formType = item.id"
              >
                {{ item.name }}
              </VBtn>
            </div>
          </VCardText>
        </VWindowItem>

        <!-- Customer Information -->
        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <div class="text-center mb-4">
              <div class="text-primary text-h5">
                {{ $t('Customer') }} Information
              </div>
              <span class="">
                Tell us about {{ $t('customer') }} details
              </span>
            </div>

            <VForm ref="contactForm">
              <VRow>
                <VCol
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="feedbackData.name"
                    :label="$t('Customer') + ' Name'"
                    :placeholder="'Enter ' + $t('Customer') + ' Name'"
                    required

                    :rules="[requiredValidator]"
                  />
                </VCol>
              
                <VCol
                  cols="12"
                  md="12"
                >
                  <AppPhoneInput
                    v-model="feedbackData.phone"
                    :label="$t('Customer') + ' Mobile Number'"
                    :placeholder="'Enter ' + $t('Customer') + ' Mobile Number'"
                    :rules="[requiredValidator]"
                    required
                  />
                  <VAlert
                    v-if="hasOtpVerification && otpVerified"
                    variant="tonal"
                    color="success"
                    density="compact"
                    class="mt-2"
                    icon="tabler-circle-check"
                  >
                    <span class="text-body-2">Mobile number verified successfully.</span>
                  </VAlert>
                  <VAlert
                    v-else-if="hasOtpVerification"
                    variant="tonal"
                    color="warning"
                    density="compact"
                    class="mt-2"
                    icon="tabler-device-mobile"
                  >
                    <span class="text-body-2">An OTP will be sent to this mobile number for verification.</span>
                  </VAlert>
                </VCol>
              
                <VCol
                  v-if="!isTenant('kochimetro')"
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="feedbackData.uhid"
                    :label="$t('Customer') + ' UHID No'"
                    :placeholder="'Enter ' + $t('Customer') + ' UHID No'"
                  />
                </VCol>
            
                <VCol
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="feedbackData.email"
                    :label="$t('Customer') + ' Email'"
                    type="email"
                    :placeholder="'Enter ' + $t('Customer') + ' Email'"
                    :rules="[emailValidator]"
                  />
                </VCol>
              
                <VCol
                  v-if="!location"
                  cols="12"
                  md="12"
                >
                  <AppTextField
                    v-model="feedbackData.location"
                    :label="$t('Customer') + ' Location'"
                    :placeholder="'Enter ' + $t('Customer') + ' Location'"
                  />
                </VCol>
                <VCol
                  v-if="hasCustomerType"
                  cols="12"
                  md="12"
                >
                  <AppSelect
                    v-model="feedbackData.customer_type"
                    :items="customerTypes"
                    label="Customer Type"
                    placeholder="Select Customer Type"
                    required
                  />
                </VCol>
                <VCol
                  v-if="hasDoctorName"
                  cols="12"
                  md="12"
                >
                  <AppSelect
                    v-model="feedbackData.doctor_name"
                    :items="doctors ?? []"
                    :item-title="(item) => item.doctor_name"
                    :item-value="(item) => item.doctor_name"
                    label="Doctor Name"
                    placeholder="Select Doctor Name"
                    :loading="fetchingDoctors"
                    :return-object="false"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VWindowItem>

        <!-- OTP Verification (conditional) -->
        <VWindowItem v-if="hasOtpVerification">
          <VCardText class="px-4 px-sm-6">
            <div class="text-center mb-6">
              <VIcon
                icon="tabler-shield-lock"
                size="48"
                color="primary"
                class="mb-3"
              />
              <div class="text-primary text-h5 mb-2">
                Verify Your Phone Number
              </div>
              <span class="text-body-2 text-medium-emphasis">
                We've sent a verification code to {{ feedbackData.phone || '' }}
              </span>
            </div>

            <div class="d-flex justify-center mb-4">
              <VOtpInput
                v-model="otpValue"
                :disabled="otpLoading"
                :length="6"
                type="number"
                class="pa-0"
                @finish="handleOtpVerify"
              />
            </div>

            <div class="text-body-2 mb-4">
              <span class="text-medium-emphasis">Didn't receive the code? </span>
              <a
                v-if="otpCooldown <= 0"
                href="#"
                class="text-primary"
                @click.prevent="resendOtp"
              >
                Resend
              </a>
              <span
                v-else
                class="text-medium-emphasis"
              >
                Resend in {{ otpCooldown }}s
              </span>
            </div>

            <VAlert
              v-if="otpError"
              type="error"
              variant="tonal"
              class="mb-4"
              closable
              @click:close="otpError = ''"
            >
              {{ otpError }}
            </VAlert>

            <div class="d-flex flex-column-reverse gap-4 justify-space-between mb-4">
              <VBtn
                variant="tonal"
                prepend-icon="tabler-arrow-left"
                @click="previousStep"
              >
                Want to change mobile number?
              </VBtn>
              <VBtn
                color="primary"
                :loading="otpLoading"
                :disabled="otpValue.length !== 6 || otpLoading"
                @click="handleOtpVerify"
              >
                Verify
              </VBtn>
            </div>
          </VCardText>
        </VWindowItem>

        <VWindowItem>
          <VCardText class="px-4 px-sm-6">
            <VForm ref="feedbackForm">
              <VSkeletonLoader
                v-if="fetchingQuestions || questionsComputed?.length === 0"
                height="500"
                loading-text="Loading..."
                indeterminate
                type="card, list-item"
              />

              <FeedbackQuestionFormV2
                v-else
                :question-groups="questionsComputed"
                type="standard"
                :nps-value="npsValue"
                :form-title="selectedFormTypeName ? selectedFormTypeName?.name + ' Feedback' : 'Write Feedback'"
                :show-fullscreen-toggle="false"
                :show-form-header="true"
                @update:answers="handleAnswersUpdate"
              />
            </VForm>
          </VCardText>
        </VWindowItem>

        <!-- Feedback Success Card -->
        <VWindowItem>
          <VCardText>
            <div>
              <div class="text-center text-primary text-h5 mb-5">
                Thank you for your feedback
              </div>
              <div class="text-body-1">
                <p
                  v-if="!isFeedbackPositive"
                  class="text-center"
                >
                  Your feedback is valuable to us. We will use this feedback to improve our services and serve you better.
                </p>
                <p
                  v-else
                  class="text-center"
                >
                  Thank you for your positive feedback. We are glad that you are satisfied with our services.
                </p>
              </div>
              <!-- if positive than show google map review button -->
              <div
                v-if="isFeedbackPositive && google_map_link"
                class="d-flex justify-center"
              >
                <VBtn
                  :loading="googleBtnLoading"
                  color="primary"
                  class="mt-5"
                  style="text-transform:none"
                  @click="googleBtnClick"
                >
                  Give us 👍 on Google
                  <VIcon icon="tabler-brand-google-maps" />
                </VBtn>
              </div>
            </div>
          </VCardText>
        </VWindowItem>
      </VWindow>

      <!-- Action Card -->
      <VCardText
        v-if="currentStep !== successStep && !(hasOtpVerification && currentStep === 3)"
        class="px-4 px-sm-6 d-flex"
        :class="{
          'justify-center': (currentStep === questionsStep && episodeNoValue) || currentStep === 0,
          'justify-space-between': !episodeNoValue && currentStep !== 0,
        }"
      >
        <VBtn
          v-show="currentStep !== 0 && currentStep < successStep && !episodeNoValue"
          variant="tonal"
          prepend-icon="tabler-arrow-left"
          @click="previousStep"
        >
          Back
        </VBtn>
        <VBtn
          v-if="currentStep < questionsStep"
          color="primary"
          variant="flat"
          append-icon="tabler-arrow-right"
          :disabled="!checkCurrentStep"
          :loading="submitBtnLoading"
          :block="currentStep == 0"
          @click="nextStep"
        >
          {{ buttonLabel }}
        </VBtn>
        <VBtn
          v-else-if="currentStep === questionsStep"
          color="primary"
          :disabled="!checkCurrentStep"
          :loading="submitBtnLoading"
          @click="nextStep"
        >
          Submit
        </VBtn>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.v-otp-input__content {
  padding: 0rem;
}

.feedback-stepper {
  .stepper-icon-step {
    .step-wrapper + svg {
      margin-inline: 3.5rem !important;
    }
  }
}

.feedback-card {
  margin-block: 6rem;
}

@media (max-width: 960px) and (min-width: 600px) {
  .feedback-page {
    .v-container {
      padding-inline: 2rem !important;
    }
  }
}

@media (max-width: 600px){
  .feedback-card {
    margin-block-start: 6rem;
  }
}

.feedback-container{
  max-inline-size: 42rem;

  // min-block-size: 100dvh;
}

@media (max-width: 1024px){
  .feedback-card {
    max-inline-size: 100%;
  }
}

.translate-wrapper {
  inline-size: 100%;

  @media (max-width: 1024px) {
    inline-size: 100%;
  }
}

.google-translate-select-flag {
  .google-translate-select-flag__mr{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__bn{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__ta{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__te{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__kn{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__ml{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }

  .google-translate-select-flag__pa{
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiBpZD0iZmxhZy1pY29ucy1pbiIgdmlld0JveD0iMCAwIDY0MCA0ODAiPgogIDxwYXRoIGZpbGw9IiNmOTMiIGQ9Ik0wIDBoNjQwdjE2MEgweiIvPgogIDxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0wIDE2MGg2NDB2MTYwSDB6Ii8+CiAgPHBhdGggZmlsbD0iIzEyODgwNyIgZD0iTTAgMzIwaDY0MHYxNjBIMHoiLz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCgzLjIgMCAwIDMuMiAzMjAgMjQwKSI+CiAgICA8Y2lyY2xlIHI9IjIwIiBmaWxsPSIjMDA4Ii8+CiAgICA8Y2lyY2xlIHI9IjE3LjUiIGZpbGw9IiNmZmYiLz4KICAgIDxjaXJjbGUgcj0iMy41IiBmaWxsPSIjMDA4Ii8+CiAgICA8ZyBpZD0iaW4tZCI+CiAgICAgIDxnIGlkPSJpbi1jIj4KICAgICAgICA8ZyBpZD0iaW4tYiI+CiAgICAgICAgICA8ZyBpZD0iaW4tYSIgZmlsbD0iIzAwOCI+CiAgICAgICAgICAgIDxjaXJjbGUgcj0iLjkiIHRyYW5zZm9ybT0icm90YXRlKDcuNSAtOC44IDEzMy41KSIvPgogICAgICAgICAgICA8cGF0aCBkPSJNMCAxNy41LjYgNyAwIDJsLS42IDV6Ii8+CiAgICAgICAgICA8L2c+CiAgICAgICAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1hIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxNSkiLz4KICAgICAgICA8L2c+CiAgICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYiIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoMzApIi8+CiAgICAgIDwvZz4KICAgICAgPHVzZSB4bGluazpocmVmPSIjaW4tYyIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgdHJhbnNmb3JtPSJyb3RhdGUoNjApIi8+CiAgICA8L2c+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgxMjApIi8+CiAgICA8dXNlIHhsaW5rOmhyZWY9IiNpbi1kIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB0cmFuc2Zvcm09InJvdGF0ZSgtMTIwKSIvPgogIDwvZz4KPC9zdmc+');
    background-size: cover;
    block-size: 100%;
    inline-size: 100%;

  }
}
</style>
